import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  constructor(
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Convierte una URL de imagen Cloudinary a formato seguro para Open Graph.
   * Los crawlers de Facebook/WhatsApp/Twitter NO soportan WebP de forma fiable,
   * así que forzamos JPG con dimensiones óptimas para social cards (1200×630).
   */
  private getOgSafeImage(imageUrl: string): string {
    if (!imageUrl) return imageUrl;

    // Si es Cloudinary, reemplazar cualquier transformación existente por una OG-safe
    if (imageUrl.includes('res.cloudinary.com')) {
      // Limpiar f_auto o f_webp,q_auto que inyecta el backend
      const cleaned = imageUrl.replace(
        /\/upload\/(f_auto|f_webp,q_auto)\//,
        '/upload/',
      );
      // Aplicar transformación OG-safe: JPG, calidad auto, 1200×630, recorte fill
      return cleaned.replace(
        '/upload/',
        '/upload/f_jpg,q_auto,w_1200,h_630,c_fill/',
      );
    }

    return imageUrl;
  }

  /**
   * Actualiza los meta tags básicos para SEO y redes sociales (Open Graph / Twitter)
   */
  updateMeta(config: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    type?: string;
  }): void {
    const title = config.title
      ? `${config.title} | MyPlace`
      : 'MyPlace - Crea y juega World Cups, Tier Lists y más';
    const description =
      config.description ||
      'La plataforma definitiva para crear torneos, tier lists y juegos interactivos con tus temas favoritos.';
    const image = config.image || 'https://www.myplacetoday.com/MyPlace.png';

    // URL segura para SSR
    let url = config.url;
    if (!url) {
      if (isPlatformBrowser(this.platformId)) {
        url = this.document.location.href;
      } else {
        // En el servidor, intentamos obtener la URL del documento inyectado
        url = this.document.URL || '';
      }
    }

    // Asegurar que la imagen sea una URL absoluta (necesario para Open Graph)
    let finalImage = image;
    if (finalImage && !finalImage.startsWith('http')) {
      const baseUrl = 'https://www.myplacetoday.com';
      finalImage = finalImage.startsWith('/')
        ? `${baseUrl}${finalImage}`
        : `${baseUrl}/${finalImage}`;
    }

    // Generar imagen OG-safe (JPG en vez de WebP para crawlers)
    const ogImage = this.getOgSafeImage(finalImage);

    const type = config.type || 'website';

    // 1. Título de la página
    this.titleService.setTitle(title);

    // 2. Metadatos estándar
    this.metaService.updateTag({ name: 'description', content: description });

    // 3. Open Graph (Facebook, WhatsApp, LinkedIn)
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({
      property: 'og:description',
      content: description,
    });
    this.metaService.updateTag({ property: 'og:image', content: ogImage });
    this.metaService.updateTag({ property: 'og:image:width', content: '1200' });
    this.metaService.updateTag({ property: 'og:image:height', content: '630' });
    this.metaService.updateTag({
      property: 'og:image:type',
      content: 'image/jpeg',
    });
    this.metaService.updateTag({ property: 'og:url', content: url });
    this.metaService.updateTag({ property: 'og:type', content: type });

    // 4. Twitter Cards
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({
      name: 'twitter:description',
      content: description,
    });
    this.metaService.updateTag({ name: 'twitter:image', content: ogImage });
  }

  /**
   * Inyecta datos estructurados (JSON-LD) en el head del documento para Google Rich Snippets
   */
  setJsonLd(schema: object): void {
    try {
      if (!this.document || !this.document.head) return;

      const existingSchema = this.document.getElementById('myplace-jsonld');
      if (existingSchema) {
        existingSchema.remove();
      }

      const script = this.document.createElement('script');
      script.id = 'myplace-jsonld';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      this.document.head.appendChild(script);
    } catch (e) {
      console.error('Error al inyectar JSON-LD:', e);
    }
  }

  /**
   * Limpia o resetea los tags a los valores por defecto
   */
  resetMeta(): void {
    this.updateMeta({});
    try {
      if (this.document) {
        const existingSchema = this.document.getElementById('myplace-jsonld');
        if (existingSchema) {
          existingSchema.remove();
        }
      }
    } catch (_) {}
  }
}
